import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CupSoda,
  KeyRound,
  Mail,
  User as UserIcon,
  Users,
} from 'lucide-react';
import { authApi } from './api';
import { OAuthButton } from './OAuthButton';
import { FlowBackground } from './FlowBackground';
import { WaveBackdrop } from './WaveBackdrop';
import { AuthFooter } from './AuthFooter';
import {
  COMPANY_SIZES,
  REGISTER_INDUSTRIES,
  registerAccountStepSchema,
  registerOrgStepSchema,
  type RegisterAccountInput,
  type RegisterOrgInput,
} from '@/lib/schemas/auth';
import { useAuthStore } from '@/lib/auth/authStore';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { FormField } from '@/components/forms/FormField';
import { LanguageMenu } from '@/components/layout/PrefsMenu';
import { toast } from '@/components/ui/Toast';
import { useHydrated } from '@/lib/hooks/useHydrated';
import { ApiError } from '@/lib/api/errors';
import { useT } from '@/lib/i18n/useT';
import { translateValidationMessage } from '@/lib/i18n/validationMessages';
import type { TranslationKey } from '@/lib/i18n/translations';
import { cn } from '@/lib/cn';
import { LEGAL_LINKS } from '@/lib/legalLinks';
import { usePlanContext } from '@onxite/plan-sdk';
import { afterAuthPath, capturePendingPlan, pendingPlanLabel, type PendingPlan } from '@/features/billing/pendingPlan';

/*
 * Alta rediseñada (sept 2026, mocks en Descargas/register/1-4.png):
 *
 *   1. Create account   — nombre completo, correo de trabajo, contraseña y el
 *                         consentimiento (1.png).
 *   2. Your organization— tarjeta Brand/Agency/Other, nombre, industria,
 *                         tamaño, región y la PRIMERA marca (2-4.png). Las
 *                         etiquetas cambian por tipo; los componentes no.
 *                         Sin «+Add»: una sola marca — el resto, después,
 *                         desde el producto.
 *   3. Get ready        — el OTP de siempre. Al verificar, el backend crea
 *                         user + company (tipo/industria/tamaño/región) + la
 *                         marca hija + plan Free.
 */

type Step = 1 | 2 | 3;
type EntityKind = RegisterOrgInput['entityKind'];

/** Lo que viaja a /auth/register/initiate (y se reutiliza para reenviar el OTP). */
interface InitiatePayload {
  companyName: string;
  firstname: string;
  lastname?: string;
  email: string;
  password: string;
  entityKind: EntityKind;
  industry: string;
  companySize: string;
  primaryRegion?: string;
  brandName: string;
}

export function RegisterPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const t = useT();
  const { reportPlanAlert } = usePlanContext();

  const [step, setStep] = useState<Step>(1);
  const [account, setAccount] = useState<RegisterAccountInput | null>(null);
  const [pending, setPending] = useState<InitiatePayload | null>(null);
  const [otp, setOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRef = useRef<HTMLInputElement>(null);

  // «Empieza gratis» en la página de precios pide el email antes de traer
  // aquí: se rellena para no pedirlo dos veces.
  const [searchParams] = useSearchParams();
  // D1: el plan elegido en xotski.com o en Precios viaja en la URL
  // (`?plan=xk1&events=…`). Se guarda al entrar para sobrevivir al OTP y, al
  // verificar, se reanuda en el pago de ese plan en vez de caer en el inicio.
  const [pendingPlan] = useState<PendingPlan | null>(() => capturePendingPlan(searchParams));
  const accountForm = useForm<RegisterAccountInput>({
    resolver: zodResolver(registerAccountStepSchema),
    defaultValues: { fullName: '', email: searchParams.get('email') ?? '', password: '', consent: false },
  });
  const orgForm = useForm<RegisterOrgInput>({
    resolver: zodResolver(registerOrgStepSchema),
    defaultValues: {
      entityKind: 'brand',
      orgName: '',
      industry: '',
      companySize: '',
      primaryRegion: '',
      brandName: '',
    },
  });

  const initiate = useMutation({
    mutationFn: (input: InitiatePayload) => authApi.register.initiate(input),
    onSuccess: (data, input) => {
      setPending(input);
      setStep(3);
      setResendCooldown(30);
      setOtp('');
      if (data.devHint) {
        toast({
          tone: 'info',
          title: t('register.toast.devHint'),
          description: data.devHint,
        });
      }
    },
    onError: (err: unknown) => {
      const status = err instanceof ApiError ? err.status : 0;
      const message = err instanceof Error ? err.message : '';

      if (status === 409 || message.includes('email-already-registered')) {
        toast({
          tone: 'error',
          title: t('register.toast.emailTaken'),
          description: t('register.toast.emailTakenBody'),
        });
        return;
      }
      if (status === 429) {
        toast({
          tone: 'error',
          title: t('register.toast.tooMany'),
          description: t('register.toast.tooManyBody'),
        });
        return;
      }
      toast({
        tone: 'error',
        title: t('register.toast.initiateFailed'),
        description: message || undefined,
      });
    },
  });

  const verify = useMutation({
    mutationFn: (code: string) =>
      authApi.register.verify({ email: pending!.email, otp: code }),
    onSuccess: (data) => {
      setSession({
        token: data.access_token,
        refreshToken: data.refresh_token ?? null,
        user: data.user,
        role: data.role,
      });
      reportPlanAlert((data as any).planAlert ?? null);
      toast({
        tone: 'success',
        title: t('register.toast.welcome', { name: pending?.firstname ?? '' }),
        description: t('register.toast.welcomeBody'),
      });
      navigate(afterAuthPath('/'));
    },
    onError: () => {
      toast({ tone: 'error', title: t('register.toast.invalidOtp') });
      setOtp('');
    },
  });

  // Auto-submit OTP when the 6 digits are typed (mirrors OtpPage behavior).
  useEffect(() => {
    if (step === 3 && otp.length === 6 && !verify.isPending) {
      verify.mutate(otp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  useEffect(() => {
    if (step === 3) otpInputRef.current?.focus();
  }, [step]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  const handleAccountSubmit = (values: RegisterAccountInput) => {
    setAccount(values);
    setStep(2);
  };

  const handleOrgSubmit = (values: RegisterOrgInput) => {
    if (!account) {
      setStep(1);
      return;
    }
    // «Full name» único en el formulario: la primera palabra es el nombre y el
    // resto, el apellido — quien firma con uno solo no inventa apellidos.
    const [firstname, ...rest] = account.fullName.trim().split(/\s+/);
    initiate.mutate({
      companyName: values.orgName,
      firstname,
      lastname: rest.join(' ') || undefined,
      email: account.email,
      password: account.password,
      entityKind: values.entityKind,
      industry: values.industry,
      companySize: values.companySize,
      primaryRegion: values.primaryRegion.trim() || undefined,
      brandName: values.brandName,
    });
  };

  const handleResend = () => {
    if (!pending || resendCooldown > 0 || initiate.isPending) return;
    // Re-send by re-initiating with the same payload — backend clears the
    // prior pending stash and issues a fresh OTP.
    initiate.mutate(pending);
  };

  return (
    <div data-surface="night" className="relative grid min-h-screen lg:grid-cols-2">
      <FlowBackground />
      {/* Left — form inside an elevated glass card so it floats above the grid. */}
      <div className="relative z-10 flex items-center justify-center px-3 py-8 sm:px-6 sm:py-12">
        <AuthFooter />
        <div
          className="w-full max-w-md rounded-2xl border border-white/[0.06] bg-[rgb(10_6_19/0.55)] p-5 shadow-[0_30px_80px_-20px_rgb(0_0_0/0.7)] backdrop-blur-xl sm:p-8"
          style={{
            boxShadow:
              '0 30px 80px -20px rgb(0 0 0 / 0.7), 0 0 0 1px color-mix(in srgb, var(--color-xk-accent) 8%, transparent) inset',
          }}
        >
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/xk-icon.svg" alt="Xotski" className="h-9 w-9 rounded-lg" />
              <span className="font-display text-lg font-bold tracking-tight">xotski</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs font-bold tracking-widest text-[var(--color-fg-muted)]">
                <span className="text-[var(--color-accent-ink)]">0{step}</span> / 03
              </span>
              <LanguageMenu />
            </div>
          </div>

          <Stepper current={step} t={t} />

          {step === 1 && (
            <StepAccount t={t} form={accountForm} onSubmit={handleAccountSubmit} pendingPlan={pendingPlan} />
          )}

          {step === 2 && (
            <StepOrganization
              t={t}
              form={orgForm}
              onSubmit={handleOrgSubmit}
              loading={initiate.isPending}
              onBack={() => setStep(1)}
            />
          )}

          {step === 3 && pending && (
            <StepVerify
              t={t}
              email={pending.email}
              otp={otp}
              onOtpChange={setOtp}
              onVerify={() => verify.mutate(otp)}
              verifying={verify.isPending}
              onResend={handleResend}
              resendCooldown={resendCooldown}
              resending={initiate.isPending}
              onBack={() => setStep(2)}
              inputRef={otpInputRef}
            />
          )}
        </div>
      </div>

      {/* Right — hero with the same composition as LoginPage. */}
      <div
        className="relative z-10 hidden overflow-hidden lg:flex lg:items-center lg:justify-center lg:p-12"
        style={{
          background:
            'linear-gradient(135deg, color-mix(in srgb, var(--color-xk-navy) 92%, transparent) 0%, color-mix(in srgb, var(--color-xk-navy-mid) 95%, transparent) 60%, color-mix(in srgb, var(--color-xk-bg-deep, var(--color-xk-navy-mid)) 92%, transparent) 100%)',
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 text-[var(--color-accent-ink)] opacity-[0.08]"
          style={{
            maskImage:
              'radial-gradient(ellipse 75% 70% at 50% 50%, black 30%, transparent 90%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 75% 70% at 50% 50%, black 30%, transparent 90%)',
          }}
        >
          <WaveBackdrop />
        </div>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 60% 55% at 50% 45%, color-mix(in srgb, var(--color-xk-accent) 18%, transparent) 0%, transparent 70%)',
          }}
        />
        <div className="relative z-10 max-w-md text-center">
          <img src="/xk-icon.svg" alt="Xotski" className="mb-6 inline-block h-20 w-20 rounded-2xl" />
          <h2 className="text-3xl font-bold">{t('register.hero.title')}</h2>
          <p className="mt-3 text-sm text-[var(--color-fg-muted)]">
            {t('register.hero.body')}
          </p>
        </div>
      </div>
    </div>
  );
}

/** La barra del mock: gradiente de progreso + los tres hitos en versalitas. */
function Stepper({ current, t }: { current: Step; t: ReturnType<typeof useT> }) {
  const labels = [
    t('register.step.create'),
    t('register.step.org'),
    t('register.step.ready'),
  ];
  return (
    <div className="mb-8">
      <div className="h-1 w-full overflow-hidden rounded-full bg-[var(--color-bg-surface)]">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${(current / 3) * 100}%`,
            background:
              'linear-gradient(90deg, var(--color-xk-accent), var(--color-accent-ink))',
          }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.14em]">
        {labels.map((label, idx) => {
          const n = (idx + 1) as Step;
          const active = current === n;
          const done = current > n;
          return (
            <span
              key={label}
              className={cn(
                done
                  ? 'text-[var(--color-accent-ink)]'
                  : active
                    ? 'text-[var(--color-fg-primary)]'
                    : 'text-[var(--color-fg-subtle)]',
              )}
            >
              {done ? '✓ ' : ''}
              {label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** Paso 1 — la cuenta (1.png). */
function StepAccount({
  t,
  form,
  onSubmit,
  pendingPlan,
}: {
  t: ReturnType<typeof useT>;
  form: ReturnType<typeof useForm<RegisterAccountInput>>;
  onSubmit: (v: RegisterAccountInput) => void;
  pendingPlan?: PendingPlan | null;
}) {
  const hydrated = useHydrated();
  const consentError = translateValidationMessage(
    form.formState.errors.consent?.message,
    t,
  );
  return (
    <>
      <h1 className="text-3xl font-bold">{t('register.acct.title')}</h1>
      <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
        {t('register.acct.subtitle')}
      </p>
      {pendingPlan && (pendingPlan.plan === 'xk1' || pendingPlan.plan === 'xk2') ? (
        <p
          data-testid="pending-plan"
          className="mt-3 inline-flex items-center gap-2 rounded-full border border-[var(--color-accent-20)] bg-[var(--color-accent-06)] px-3 py-1.5 text-xs font-medium text-[var(--color-accent-ink)]"
        >
          <span className="font-mono text-[10px] font-bold uppercase tracking-widest">{pendingPlanLabel(pendingPlan)}</span>
          {t('register.pendingPlan', { plan: pendingPlanLabel(pendingPlan) })}
        </p>
      ) : null}

      <div className="mt-6">
        <div className="relative flex items-center">
          <div className="flex-grow border-t border-[var(--color-border-default)]" />
          <span className="mx-3 text-xs uppercase tracking-wider text-[var(--color-fg-subtle)]">
            {t('register.orWithSocial')}
          </span>
          <div className="flex-grow border-t border-[var(--color-border-default)]" />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <OAuthButton provider="google" label="Google" />
          <OAuthButton provider="linkedin" label="LinkedIn" />
          <OAuthButton provider="facebook" label="Facebook" comingSoon />
          <OAuthButton provider="apple" label="Apple" comingSoon />
        </div>
      </div>

      <div className="my-6 relative flex items-center">
        <div className="flex-grow border-t border-[var(--color-border-default)]" />
        <span className="mx-3 text-xs uppercase tracking-wider text-[var(--color-fg-subtle)]">
          {t('register.orWithEmail')}
        </span>
        <div className="flex-grow border-t border-[var(--color-border-default)]" />
      </div>

      <form noValidate className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label={t('register.acct.fullName')}
            error={form.formState.errors.fullName?.message}
            required
          >
            <div className="relative">
              <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
              <Input
                type="text"
                autoFocus
                autoComplete="name"
                placeholder={t('register.acct.fullNamePlaceholder')}
                className="pl-9"
                {...form.register('fullName')}
              />
            </div>
          </FormField>
          <FormField
            label={t('register.acct.email')}
            error={form.formState.errors.email?.message}
            required
          >
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
              <Input
                type="email"
                autoComplete="email"
                placeholder="you@brand.com"
                className="pl-9"
                {...form.register('email')}
              />
            </div>
          </FormField>
        </div>
        <FormField
          label={t('register.acct.password')}
          error={form.formState.errors.password?.message}
          required
        >
          <div className="relative">
            <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
            <Input
              type="password"
              autoComplete="new-password"
              placeholder={t('register.acct.passwordPlaceholder')}
              className="pl-9"
              {...form.register('password')}
            />
          </div>
        </FormField>

        {/* El consentimiento del mock, con los dos documentos enlazados. */}
        <div>
          <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-[var(--color-fg-muted)]">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 flex-none accent-[var(--color-accent)]"
              {...form.register('consent')}
            />
            <span>
              {t('register.acct.consentPre')}
              <a
                href={LEGAL_LINKS.terms}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-[var(--color-fg-primary)] underline underline-offset-2"
              >
                {t('register.acct.consentTerms')}
              </a>
              {t('register.acct.consentMid')}
              <a
                href={LEGAL_LINKS.privacy}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-[var(--color-fg-primary)] underline underline-offset-2"
              >
                {t('register.acct.consentPrivacy')}
              </a>
              {t('register.acct.consentPost')}
            </span>
          </label>
          {consentError ? (
            <p className="mt-1 text-xs text-[var(--color-danger,#f87171)]" role="alert">
              {consentError}
            </p>
          ) : null}
        </div>

        {/* La fila del mock: «Have an account? Sign in» + «Create account →». */}
        <div className="flex items-center justify-between gap-4 border-t border-[var(--color-border-default)] pt-5">
          <p className="text-sm text-[var(--color-fg-muted)]">
            {t('register.haveAccount')}{' '}
            <Link
              to="/login"
              className="font-semibold text-[var(--color-accent-ink)] hover:underline"
            >
              {t('register.signIn')}
            </Link>
          </p>
          <Button
            type="submit"
            size="lg"
            disabled={!hydrated}
            rightIcon={<ArrowRight className="h-4 w-4" />}
          >
            {t('register.acct.create')}
          </Button>
        </div>
      </form>
    </>
  );
}

/**
 * Paso 2 — la organización (2-4.png). Un solo formulario para los tres tipos:
 * lo que cambia con la tarjeta elegida son las ETIQUETAS, nunca los campos.
 */
function StepOrganization({
  t,
  form,
  onSubmit,
  loading,
  onBack,
}: {
  t: ReturnType<typeof useT>;
  form: ReturnType<typeof useForm<RegisterOrgInput>>;
  onSubmit: (v: RegisterOrgInput) => void;
  loading: boolean;
  onBack: () => void;
}) {
  const hydrated = useHydrated();
  const kind = form.watch('entityKind');
  /** Etiqueta por tipo: `register.org.<campo>.<kind>`. */
  const kk = (base: string) => t(`${base}.${kind}` as TranslationKey);

  const KINDS: Array<{
    id: EntityKindOption;
    icon: typeof CupSoda;
    title: TranslationKey;
    hint: TranslationKey;
  }> = [
    { id: 'brand', icon: CupSoda, title: 'register.org.kind.brand', hint: 'register.org.kind.brandHint' },
    { id: 'agency', icon: Users, title: 'register.org.kind.agency', hint: 'register.org.kind.agencyHint' },
  ];

  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="mb-4 inline-flex items-center gap-1 text-sm text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
      >
        <ArrowLeft className="h-4 w-4" /> {t('common.back')}
      </button>

      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--color-accent-ink)]">
        {t('register.org.kicker')}
      </p>
      <h1 className="mt-1 text-3xl font-bold">{t('register.org.title')}</h1>
      <p className="mt-2 text-sm text-[var(--color-fg-muted)]">{t('register.org.body')}</p>

      <form noValidate className="mt-6 space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <div>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--color-fg-subtle)]">
            {t('register.org.kindLabel')}
          </p>
          <div role="radiogroup" className="grid grid-cols-2 gap-2">
            {KINDS.map(({ id, icon: Icon, title, hint }) => {
              const active = kind === id;
              return (
                <label
                  key={id}
                  className={cn(
                    'flex cursor-pointer flex-col gap-2 rounded-xl border p-3 transition-colors',
                    active
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)]'
                      : 'border-[var(--color-border-default)] hover:border-[var(--color-border-strong,var(--color-border-default))]',
                  )}
                >
                  <input
                    type="radio"
                    value={id}
                    className="sr-only"
                    {...form.register('entityKind')}
                  />
                  <span
                    className={cn(
                      'grid h-9 w-9 place-items-center rounded-lg',
                      active
                        ? 'bg-[var(--color-accent)] text-[var(--color-fg-on-accent)]'
                        : 'bg-[var(--color-bg-surface)] text-[var(--color-fg-muted)]',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-bold text-[var(--color-fg-primary)]">
                    {t(title)}
                  </span>
                  <span className="text-[11px] leading-snug text-[var(--color-fg-muted)]">
                    {t(hint)}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <FormField
          label={kk('register.org.orgName')}
          error={form.formState.errors.orgName?.message}
          required
        >
          <Input
            type="text"
            placeholder={kk('register.org.orgPlaceholder')}
            {...form.register('orgName')}
          />
        </FormField>

        <FormField
          label={kk('register.org.industry')}
          error={form.formState.errors.industry?.message}
          required
        >
          <Select {...form.register('industry')}>
            <option value="">{t('register.org.industryPlaceholder')}</option>
            {REGISTER_INDUSTRIES.map((ind) => (
              <option key={ind.value} value={ind.value}>
                {t(ind.key as TranslationKey)}
              </option>
            ))}
          </Select>
        </FormField>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label={t('register.org.companySize')}
            error={form.formState.errors.companySize?.message}
            required
          >
            <Select {...form.register('companySize')}>
              <option value="">{t('register.org.companySizePlaceholder')}</option>
              {COMPANY_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField
            label={t('register.org.region')}
            error={form.formState.errors.primaryRegion?.message}
          >
            <Input
              type="text"
              placeholder={t('register.org.regionPlaceholder')}
              {...form.register('primaryRegion')}
            />
          </FormField>
        </div>

        {/* UNA marca: el «+Add» del mock se retiró a propósito. */}
        <FormField
          label={kk('register.org.brand')}
          error={form.formState.errors.brandName?.message}
          required
          hint={kk('register.org.brandHint')}
        >
          <Input
            type="text"
            placeholder={kk('register.org.brandPlaceholder')}
            {...form.register('brandName')}
          />
        </FormField>

        <Button
          type="submit"
          loading={loading}
          disabled={!hydrated}
          className="w-full"
          size="lg"
          rightIcon={<ArrowRight className="h-4 w-4" />}
        >
          {t('register.org.continue')}
        </Button>
      </form>
    </>
  );
}

type EntityKindOption = RegisterOrgInput['entityKind'];

/** Paso 3 — «Get ready»: el OTP de verificación de siempre. */
function StepVerify({
  t,
  email,
  otp,
  onOtpChange,
  onVerify,
  verifying,
  onResend,
  resendCooldown,
  resending,
  onBack,
  inputRef,
}: {
  t: ReturnType<typeof useT>;
  email: string;
  otp: string;
  onOtpChange: (v: string) => void;
  onVerify: () => void;
  verifying: boolean;
  onResend: () => void;
  resendCooldown: number;
  resending: boolean;
  onBack: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="mb-4 inline-flex items-center gap-1 text-sm text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
      >
        <ArrowLeft className="h-4 w-4" /> {t('common.back')}
      </button>

      <div className="mb-6 grid h-16 w-16 place-items-center rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]">
        <UserIcon className="h-7 w-7" />
      </div>

      <h1 className="text-2xl font-bold">{t('register.verify.title')}</h1>
      <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
        {t('register.verify.subtitlePrefix')}{' '}
        <span className="font-semibold text-[var(--color-accent-ink)]">{email}</span>
        {'.'}
      </p>

      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={6}
        value={otp}
        onChange={(e) => onOtpChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="······"
        className="mt-8 h-16 w-full rounded-xl border border-[var(--color-border-default)] bg-black/40 text-center font-mono text-3xl font-bold tracking-[0.6em] text-[var(--color-accent-ink)] placeholder:text-[var(--color-fg-disabled)] focus:border-[var(--color-accent-ink)] focus:outline-none"
      />

      <Button
        type="button"
        className="mt-6 w-full"
        size="lg"
        loading={verifying}
        disabled={otp.length !== 6}
        onClick={onVerify}
      >
        {t('register.verify.verify')}
      </Button>

      <div className="mt-6 text-center text-sm text-[var(--color-fg-muted)]">
        {t('register.verify.didntReceive')}{' '}
        <button
          type="button"
          disabled={resendCooldown > 0 || resending}
          onClick={onResend}
          className="font-semibold text-[var(--color-accent-ink)] disabled:text-[var(--color-fg-subtle)]"
        >
          {resendCooldown > 0
            ? t('register.verify.resendIn', { seconds: resendCooldown })
            : t('register.verify.resendNow')}
        </button>
      </div>
    </>
  );
}
